/**
 * Atlas — MFA Enrollment page (forced for admins; voluntary for other roles).
 *
 * Two modes:
 *
 *  1. **Bootstrap mode** — entered from /login when the backend signals
 *     `mfa_enrollment_required`. The component receives the user's email
 *     and password in router state (NOT localStorage — wiped on refresh).
 *     It calls /auth/mfa/enroll-init with credentials, displays the QR,
 *     then calls /auth/mfa/enroll-confirm to activate.
 *
 *  2. **Logged-in mode** — entered from account settings by a user who
 *     wants to enable MFA voluntarily (e.g. a pro). Uses /auth/mfa/enroll
 *     and /auth/mfa/confirm which require an active session.
 *
 * See SECURITY_AUDIT H-4 — MFA is mandatory for the admin role.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, clearAccessToken } from '../services/api';

function extractError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail) && detail.length > 0) {
    return detail[0]?.msg || 'Invalid request.';
  }
  return 'Something went wrong.';
}

export default function MFAEnrollPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Credentials forwarded from the login page when MFA enrollment is required.
  // If creds were passed via router state we ALWAYS use bootstrap mode, even
  // if a stale refresh cookie has silently re-hydrated `user`. Otherwise the
  // page would call the authenticated /mfa/confirm with a token whose
  // token_version was just bumped by enroll-init → "Session expired".
  const bootstrap = location.state || null;
  const isBootstrap = !!bootstrap?.email && !!bootstrap?.password;
  const isAuthenticated = !isBootstrap && !!user;

  const [phase, setPhase] = useState('init');   // 'init' | 'confirm' | 'done'
  const [secret, setSecret] = useState('');
  const [otpauthUri, setOtpauthUri] = useState('');
  const [enrollToken, setEnrollToken] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const ran = useRef(false);

  // If neither bootstrap-creds nor an authenticated session is available,
  // there's nothing to do — send the user back to login.
  useEffect(() => {
    if (!isBootstrap && !isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isBootstrap, isAuthenticated, navigate]);

  // Kick off enrollment as soon as we land here.
  useEffect(() => {
    if (ran.current) return;
    if (!isBootstrap && !isAuthenticated) return;
    ran.current = true;

    // In bootstrap mode, wipe any stale access token so requests to the
    // anonymous /mfa/enroll-init and /mfa/enroll-confirm endpoints don't
    // carry a stale Authorization header. (A stale refresh cookie can
    // re-hydrate _accessToken via silentRefresh on AuthContext mount.)
    if (isBootstrap) {
      clearAccessToken();
    }

    setLoading(true);
    const initFn = isBootstrap
      ? authAPI.mfaEnrollInit(bootstrap.email, bootstrap.password)
      : authAPI.mfaEnroll();

    initFn
      .then((res) => {
        const data = res.data;
        setSecret(data.secret);
        setOtpauthUri(data.provisioning_uri);
        if (isBootstrap) setEnrollToken(data.enroll_token);
        setPhase('confirm');
      })
      .catch((err) => setError(extractError(err)))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConfirm = async (e) => {
    e.preventDefault();
    if (code.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      if (isBootstrap) {
        await authAPI.mfaEnrollConfirm(enrollToken, code);
      } else {
        await authAPI.mfaConfirm(code);
      }
      setPhase('done');
    } catch (err) {
      setError(extractError(err));
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* ignored */ }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--paper)', padding: 20 }}>
      <div style={{ width: '100%', maxWidth: 480 }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            <img src="/brand/svg/propatlas-mark.svg" className="brand-mark" alt="PropAtlas mark" style={{ width: 34 }} />
            <span className="brand-name" style={{ fontSize: 28 }}>Prop<em>A</em>tlas</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-4)', marginTop: 6 }}>
            Set up two-factor authentication
          </div>
        </div>

        <div className="panel" style={{ padding: 32 }}>
          {phase === 'init' && loading && (
            <div style={{ textAlign: 'center', padding: 20 }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid var(--hairline)', borderTopColor: 'var(--forest)', margin: '0 auto 20px', animation: 'spin 0.8s linear infinite' }} />
              <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Generating your secret…</div>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          )}

          {phase === 'init' && !loading && error && (
            <div>
              <div style={{ padding: 12, background: 'rgba(139,46,46,0.08)', border: '1px solid rgba(139,46,46,0.2)', borderRadius: 4, color: 'var(--crimson)', fontSize: 13, marginBottom: 16 }}>
                {error}
              </div>
              <Link to="/login" className="btn" style={{ textDecoration: 'none', width: '100%', justifyContent: 'center' }}>← Back to login</Link>
            </div>
          )}

          {phase === 'confirm' && (
            <>
              <ol style={{ paddingLeft: 18, margin: '0 0 20px', color: 'var(--ink-2)', fontSize: 14, lineHeight: 1.7 }}>
                <li>Open your authenticator app (Authy, Google Authenticator, 1Password, etc.)</li>
                <li>Scan the QR code below, or enter the secret manually</li>
                <li>Type the 6-digit code your app generates</li>
              </ol>

              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                <div style={{ padding: 12, background: '#fff', border: '1px solid var(--hairline)', borderRadius: 8 }}>
                  <QRCodeSVG value={otpauthUri} size={192} level="M" includeMargin={false} />
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-4)', marginBottom: 6 }}>
                  Or enter this secret manually
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <code style={{
                    flex: 1, padding: '10px 12px', background: 'var(--paper-2)', border: '1px solid var(--hairline)',
                    borderRadius: 'var(--r-sm)', fontFamily: 'var(--mono)', fontSize: 13,
                    wordBreak: 'break-all', color: 'var(--ink-1)',
                  }}>
                    {secret}
                  </code>
                  <button type="button" onClick={copySecret} className="btn sm">
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                </div>
              </div>

              <form onSubmit={handleConfirm} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {error && (
                  <div style={{ padding: 10, background: 'rgba(139,46,46,0.08)', border: '1px solid rgba(139,46,46,0.2)', borderRadius: 4, color: 'var(--crimson)', fontSize: 13 }}>
                    {error}
                  </div>
                )}
                <div className="field">
                  <label>Verification code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]{6}"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    required
                    autoFocus
                    placeholder="123456"
                    style={{ letterSpacing: '0.25em', textAlign: 'center', fontFamily: 'var(--mono)', fontSize: 18 }}
                  />
                </div>
                <button
                  type="submit"
                  className="btn primary lg"
                  disabled={loading || code.length !== 6}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {loading ? 'Activating…' : 'Activate MFA'}
                </button>
              </form>
            </>
          )}

          {phase === 'done' && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'var(--forest)', color: 'var(--paper)', display: 'grid', placeItems: 'center', fontSize: 28, margin: '0 auto 20px' }}>✓</div>
              <div style={{ fontFamily: 'var(--serif)', fontSize: 22, fontWeight: 700, color: 'var(--forest)', marginBottom: 12 }}>
                MFA enabled
              </div>
              <p style={{ fontSize: 14, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 24 }}>
                {isBootstrap
                  ? 'Two-factor authentication is now required for your account. Sign in again and enter your code when prompted.'
                  : 'You will be prompted for a 6-digit code from your authenticator app every time you sign in.'}
              </p>
              <Link to="/login" className="btn primary" style={{ textDecoration: 'none' }}>
                Continue to sign in
              </Link>
            </div>
          )}
        </div>

        {phase !== 'done' && (
          <div style={{ textAlign: 'center', marginTop: 16, fontSize: 12, color: 'var(--ink-4)' }}>
            Lost access to your authenticator? Contact <a href="mailto:support@propatlas.com.au" style={{ color: 'var(--forest)' }}>support@propatlas.com.au</a>.
          </div>
        )}
      </div>
    </div>
  );
}
